"use client";

import React from "react";
import { Sparkles, Cpu, GraduationCap, Trophy, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function VisibleResults() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="225908" id="about-me" className="w-full bg-background py-20 sm:py-24 md:py-28 lg:py-32 overflow-hidden scroll-mt-20">
      <style>{`
        @keyframes pulse-border-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(0, 255, 93, 0.3), inset 0 0 10px rgba(0, 255, 93, 0.2); border-color: rgba(0, 255, 93, 0.5); }
          50% { box-shadow: 0 0 25px rgba(0, 255, 93, 0.6), inset 0 0 15px rgba(0, 255, 93, 0.4); border-color: rgba(0, 255, 93, 1); }
        }
        @keyframes glitch-flicker {
          0% { opacity: 0.8; transform: scaleY(1); }
          5% { opacity: 0.2; transform: scaleY(1.1); }
          10% { opacity: 0.8; transform: scaleY(1); }
          15% { opacity: 0.4; transform: scaleY(0.9); }
          20% { opacity: 0.9; transform: scaleY(1); }
          100% { opacity: 0.8; transform: scaleY(1); }
        }
        @keyframes hud-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        .scanlines-overlay {
          background: repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.5) 0px,
            rgba(0, 0, 0, 0.5) 2px,
            transparent 2px,
            transparent 4px
          );
        }
      `}</style>
      <div className="container mx-auto px-4 md:px-6">
        <div ref={contentRef} className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-start transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          
          <div className="relative group order-2 md:order-1 justify-self-center">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary via-secondary to-accent blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
            <div className="max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg">
              <div className="relative aspect-[3/4] overflow-hidden border bg-card transition-all duration-500" style={{
              animation: 'pulse-border-glow 3s infinite ease-in-out'
            }}>
                <img src="https://sway-systems.com/wp-content/uploads/2026/05/IMG_2689-1.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652639/img/workspace_repair_glow.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652639/img/workspace_repair_glow.png-wvc-sizes" alt="Technical Workspace" className="w-full h-full object-cover transition-all duration-700" loading="lazy" />

                <div className="absolute inset-0 pointer-events-none scanlines-overlay opacity-50"></div>

                <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-[#00ff5d]"></div>
                <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-[#00ff5d]"></div>
                <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-[#00ff5d]"></div>
                <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-[#00ff5d]"></div>

                <div className="absolute top-0 right-0 w-[4px] h-full bg-gradient-to-b from-transparent via-[#00ff5d] to-transparent opacity-80" style={{
                animation: 'glitch-flicker 2s infinite linear'
              }}></div>

                <div className="absolute bottom-4 left-4 font-mono text-[10px] text-[#00ff5d] leading-tight pointer-events-none">
                  <div className="bg-black/60 px-1.5 py-0.5 mb-1 border-l border-[#00ff5d]">
                    [ ID: OPERATOR ]
                  </div>
                  <div className="bg-black/60 px-1.5 py-0.5 border-l border-[#00ff5d]">
                    STATUS: ACTIVE <span style={{
                    animation: 'hud-blink 1s step-end infinite'
                  }}>▮</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col space-y-6 order-1 md:order-2">
            <div className="inline-flex items-center w-fit gap-2 px-3 py-1 bg-primary/10 border border-primary/30 text-xs font-default font-medium text-primary tracking-widest uppercase">
              <Cpu className="size-3 animate-pulse" />
              <span>Operator Profile: Active</span>
            </div>

            <h2 className="font-default text-4xl sm:text-2xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground tracking-tighter">
              ABOUT <span className="text-primary">ME</span>
            </h2>

            <div className="space-y-4">
              <p className="font-body-sans text-lg text-muted-foreground leading-relaxed">
                I am a Computer Science graduate from McMurry University in Abilene, Texas, and a former esports player. My background blends academic rigor with high-stakes competitive performance.
              </p>
              <p className="font-body-sans text-lg text-muted-foreground leading-relaxed">
                With hands-on experience in hardware upgrades, complex troubleshooting, and work as a development technician at Dell, I focus on delivering simple, reliable solutions for every system I touch.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4 pt-4">
              <div className="flex items-center gap-3 p-3 border border-primary/10 bg-primary/5">
                <GraduationCap className="size-5 text-secondary" />
                <span className="text-xs font-default uppercase tracking-wider text-foreground">CS Graduate</span>
              </div>
              <div className="flex items-center gap-3 p-3 border border-primary/10 bg-primary/5">
                <Trophy className="size-5 text-secondary" />
                <span className="text-xs font-default uppercase tracking-wider text-foreground">Former Esports Player</span>
              </div>
              <div className="flex items-center gap-3 p-3 border border-primary/10 bg-primary/5">
                <Wrench className="size-5 text-secondary" />
                <span className="text-xs font-default uppercase tracking-wider text-foreground">Hardware Specialist</span>
              </div>
              <div className="flex items-center gap-3 p-3 border border-primary/10 bg-primary/5">
                <Sparkles className="size-5 text-secondary" />
                <span className="text-xs font-default uppercase tracking-wider text-foreground">Development Technician at Dell</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}